import {
  GAIT_SPEED_BAR_GRADIENT_CSS,
  GAIT_SPEED_BAR_SCALE_TICKS,
  GAIT_SPEED_MCID_RANGE_MS,
  GAIT_SPEED_REFERENCE_NOTE,
  formatGaitSpeed,
  formatGaitSpeedSeconds,
  getGaitSpeedAttemptEntries,
  getGaitSpeedBarProgress,
  getGaitSpeedReferenceComparisonNote,
  getGaitSpeedScoreColor,
  getGaitSpeedSeverityLabel,
  getGaitSpeedSeverityRangeLabel,
  getGaitSpeedSeverityTone,
  normalizeGaitSpeedPayload,
  summarizeGaitSpeedResult,
  type GaitSpeedAttemptPayload,
  type GaitSpeedResultPayload,
} from '../gait-speed/gaitSpeedDomain';
import AssessmentHistoryComparisonCard, { type AssessmentHistoryComparisonEntry } from '../components/AssessmentHistoryComparisonCard';

export { getGaitSpeedSeverityLabel, getGaitSpeedSeverityTone };

interface GaitSpeedResultRendererProps {
  payload: GaitSpeedResultPayload | null | undefined;
  assessmentDate?: string | null;
  comparisonHistory?: AssessmentHistoryComparisonEntry[];
}

const AttemptCard = ({ label, attempt, highlight = false }: { label: string; attempt: GaitSpeedAttemptPayload; highlight?: boolean }) => {
  const severity = getGaitSpeedSeverityLabel(attempt.speedMs, attempt.completed !== false, Boolean(attempt.physicalAssistance));
  const severityTone = getGaitSpeedSeverityTone(attempt.speedMs, attempt.completed !== false, Boolean(attempt.physicalAssistance));
  const rangeLabel = getGaitSpeedSeverityRangeLabel(attempt.speedMs, attempt.completed !== false, Boolean(attempt.physicalAssistance));

  return (
    <div className={`rounded-[24px] border bg-white p-5 shadow-sm ${highlight ? 'border-primary-200' : 'border-slate-100'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
          <div className="mt-2 text-3xl font-black text-slate-900">
            {attempt.completed !== false && attempt.speedMs != null ? `${formatGaitSpeed(attempt.speedMs)} m/s` : 'Não concluiu'}
          </div>
          {attempt.timeSeconds != null ? <div className="mt-1 text-sm font-medium text-slate-500">{formatGaitSpeedSeconds(attempt.timeSeconds)} s</div> : null}
        </div>
        <div className={`rounded-full px-3 py-1.5 text-xs font-bold shadow-sm ${severityTone}`}>{severity}</div>
      </div>

      <div className="mt-4 grid gap-3 text-sm text-slate-600 md:grid-cols-2">
        <div>
          <span className="font-medium text-slate-900">Faixa:</span> {rangeLabel}
        </div>
        <div>
          <span className="font-medium text-slate-900">Auxílio:</span> {attempt.walkingAidLabel}
        </div>
        <div>
          <span className="font-medium text-slate-900">Oxigênio:</span> {attempt.oxygenUsed ? 'Sim' : 'Não'}
        </div>
        <div>
          <span className="font-medium text-slate-900">Assistência física:</span> {attempt.physicalAssistance ? 'Sim' : 'Não'}
        </div>
      </div>

      {attempt.reasonNotCompleted ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Motivo registrado: {attempt.reasonNotCompleted}
        </div>
      ) : null}

      {attempt.observations ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">Observações</div>
          <div className="mt-2 whitespace-pre-wrap">{attempt.observations}</div>
        </div>
      ) : null}
    </div>
  );
};

export default function GaitSpeedResultRenderer({ payload, assessmentDate = null, comparisonHistory = [] }: GaitSpeedResultRendererProps) {
  const normalized = normalizeGaitSpeedPayload(payload);
  const summary = summarizeGaitSpeedResult(payload);
  const attempts = getGaitSpeedAttemptEntries(payload);
  const speedMs = summary.bestSpeedMs;
  const scoreColor = getGaitSpeedScoreColor(speedMs, summary.completed, summary.physicalAssistance, summary.referenceComparison);
  const markerProgress = getGaitSpeedBarProgress(speedMs);
  const bestAttemptLabel = summary.bestAttempt ? `${summary.bestAttempt}ª tentativa` : '—';
  const activeFlags = summary.clinicalFlags.filter((flag) => flag.active);
  const referenceComparison = summary.referenceComparison;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Velocidade de marcha em 4 metros</p>
          <div className="text-6xl font-black leading-none" style={{ color: scoreColor }}>
            {summary.completed && speedMs != null ? formatGaitSpeed(speedMs) : '—'}
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-700">{summary.completed && speedMs != null ? 'm/s' : 'não concluído'}</div>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold" style={{ color: scoreColor }}>
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            {summary.severity}
          </div>
          <div className="mt-3 text-xs font-medium text-slate-500">
            {summary.attemptCount > 1 ? `Melhor de ${summary.attemptCount} tentativas • ${bestAttemptLabel}` : '1 tentativa registrada'} •{' '}
            {summary.walkingAidLabel.toLocaleLowerCase('pt-BR')}
          </div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{summary.interpretation}</p>
          </div>

          <div className="space-y-2">
            <div className="relative py-1">
              <div
                className="h-9 rounded-full"
                style={{
                  background: GAIT_SPEED_BAR_GRADIENT_CSS,
                }}
              />
              {markerProgress != null && (
                <div className="absolute -top-2 h-[calc(100%+16px)] w-1 rounded-full bg-slate-800" style={{ left: `calc(${markerProgress * 100}% - 2px)` }} />
              )}
            </div>
            <div className="relative h-5 text-xs font-medium text-slate-500">
              {GAIT_SPEED_BAR_SCALE_TICKS.map((tick, index) => (
                <span
                  key={tick.label}
                  className="absolute top-0 whitespace-nowrap"
                  style={{
                    left: `${tick.position * 100}%`,
                    transform: index === 0 ? 'none' : index === GAIT_SPEED_BAR_SCALE_TICKS.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                  }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            Faixa exibida: <span className="font-semibold text-slate-900">{summary.severityRange}</span>
          </div>
        </div>
      </div>

      <AssessmentHistoryComparisonCard testKey="4MGS" currentPayload={payload} currentAssessmentDate={assessmentDate} history={comparisonHistory} />

      <div className={`grid gap-6 ${activeFlags.length ? 'xl:grid-cols-2' : ''}`}>
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Referência prevista</div>
          <div className="mt-2 text-xl font-black text-slate-900">{summary.referenceStatus}</div>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{getGaitSpeedReferenceComparisonNote(referenceComparison)}</p>
        </div>

        {activeFlags.length ? (
          <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Alertas clínicos</div>
            <div className="mt-4 space-y-3">
              {activeFlags.map((flag) => (
                <div key={flag.key} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="text-sm font-black text-amber-950">
                    {flag.label} <span className="font-semibold text-amber-800">({flag.thresholdLabel})</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-amber-900/80">{flag.description}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {attempts.length ? (
        <div className="space-y-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Tentativas registradas</div>
          <div className="grid gap-6 xl:grid-cols-2">
            {attempts.map((entry) => (
              <AttemptCard
                key={`gait-speed-attempt-${entry.index}`}
                label={`${entry.index}ª tentativa${summary.bestAttempt === entry.index ? ' • melhor velocidade' : ''}`}
                attempt={entry.attempt}
                highlight={summary.bestAttempt === entry.index}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Critério</th>
              <th className="px-6 py-4">Leitura clínica</th>
              <th className="px-6 py-4">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {[
              [
                '≥ 0,8 m/s',
                'Velocidade preservada pelo ponto de corte usado em DPOC; comparar ao previsto quando houver idade e IMC/peso.',
                summary.completed && !summary.physicalAssistance && speedMs != null && speedMs >= 0.8 && referenceComparison?.withinNormalLimit !== false,
              ],
              [
                '< 0,8 m/s',
                'Marcha lenta em DPOC, associada a pior capacidade de exercício, dispneia e qualidade de vida.',
                activeFlags.some((flag) => flag.key === 'slow_gait'),
              ],
              [
                '< 78% previsto',
                'Abaixo do limite inferior de normalidade pela equação brasileira do 4MGS usual.',
                activeFlags.some((flag) => flag.key === 'below_reference'),
              ],
              [
                `MCID ${GAIT_SPEED_MCID_RANGE_MS}`,
                'Mudança aproximada entre avaliações considerada clinicamente importante em DPOC.',
                false,
              ],
            ].map(([range, note, selected]) => (
              <tr key={String(range)} className={selected ? 'bg-emerald-50/60' : 'hover:bg-slate-50/60'}>
                <td className="px-6 py-4 font-black text-slate-900">{range}</td>
                <td className="px-6 py-4 text-slate-700">{note}</td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex rounded-lg px-2.5 py-1 text-[10px] font-black uppercase ${
                      selected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {selected ? 'Critério atual' : 'Referência'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Protocolo registrado</div>
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span>Distância</span>
              <span className="font-semibold text-slate-900">{normalized?.protocol?.distanceMeters ?? 4} m</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Ritmo</span>
              <span className="font-semibold text-slate-900">Habitual</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Melhor tentativa</span>
              <span className="font-semibold text-slate-900">{bestAttemptLabel}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Oxigênio</span>
              <span className="font-semibold text-slate-900">{summary.oxygenUsed ? 'Sim' : 'Não'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Assistência física</span>
              <span className="font-semibold text-slate-900">{summary.physicalAssistance ? 'Sim' : 'Não'}</span>
            </div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Base de interpretação</div>
          <p className="mt-4 text-sm leading-relaxed text-slate-700">
            {typeof normalized?.referenceNote === 'string' && normalized.referenceNote.trim().length > 0 ? normalized.referenceNote : GAIT_SPEED_REFERENCE_NOTE}
          </p>
          {normalized?.observations ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">Observações registradas</div>
              <div className="mt-2 whitespace-pre-wrap">{normalized.observations}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
